import React, { FC, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import {
	Settings,
	Platform,
	DeviceType,
	Orientation,
	Lightbox,
	ShoppingItem,
} from 'sofa-reborn-react';
import { ProductData } from '@ikea-aoa/component-view-3d';
import ProductCardModalEnum from '@inter-ikea-kompis/component-product-card/lib/enums/ProductCardModalEnum';
import AdditionalInformation from '../components/AdditionalInformation/AdditionalInformation';
import { setCurrentLightbox, setThumbnailImageChanged } from '../store/application/actions';
import { selectSceneItem } from '../store/scene/actions';
import {
	IShoppingProduct,
	IStoreAvailability,
	IStore,
	IDexfSettings,
	ITranslations,
} from '@inter-ikea-kompis/types';
import {
	ProductService,
	KompisDefaultFields,
	CheckoutAvailabilityService,
	StoreService,
} from '@inter-ikea-kompis/services';
import { setStoreId } from '../store/localization/actions';
import { ProductOptionalFieldNameEnum } from '@inter-ikea-kompis/enums';
import { ApplicationUtils } from '../util/ApplicationUtils';

const sofaFields = [...KompisDefaultFields, ProductOptionalFieldNameEnum.complianceInformation];

const AdditionalInformationContainer: FC = () => {
	const settings = useSelector<RootState, Settings>((state) => state.settings.settings);
	const selectedArticle = useSelector<RootState, string>(
		(state) => state.scene.selectedSceneArticle as string
	);
	const productData = useSelector<RootState, ProductData>((state) => state.products.products);
	const productItem = {
		id: selectedArticle,
		type: productData[selectedArticle].type,
	};
	const shoppingItems = [
		{
			id: selectedArticle.replace('_mirror', ''),
			type: productData[selectedArticle].type,
			quantity: 1,
		},
	];
	const platform = useSelector<RootState, Platform>((state) => state.deviceInfo.platform);
	const deviceType = useSelector<RootState, DeviceType>((state) => state.deviceInfo.deviceType);
	const orientation = useSelector<RootState, Orientation>((state) => state.deviceInfo.orientation);
	const currentLightbox = useSelector<RootState, Lightbox>(
		(state) => state.application.currentLightbox
	);
	const showScreensaver = useSelector<RootState, boolean>(
		(state) => state.screensaver.showScreensaver
	);
	const kompisSettings = useSelector<RootState, IDexfSettings | null>(
		(state) => state.settings.kompisSettings
	);
	const kompisTranslations = useSelector<RootState, ITranslations | null>(
		(state) => state.localization.kompisTranslations
	);
	const coverOnly = useSelector<RootState, boolean | undefined>((state) => state.cover.coverOnly);
	const storeId = useSelector<RootState, string>((state) => state.localization.storeId as string);
	const isProduction = ApplicationUtils.isProduction();

	/* Used to get sheet/lightbox animation */
	const [visible, setVisible] = useState(false);
	const dispatch = useDispatch<AppDispatch>();
	const [carouselSelectedIndex, setCarouselSelectedIndex] = useState(0);
	const [shoppingProducts, setShoppingProducts] = useState<IShoppingProduct[] | []>([]);
	const [visibleModal, setVisibleModal] = useState<ProductCardModalEnum | null | undefined>(null);
	const [storeAvailability, setStoreAvailability] = useState<IStoreAvailability | null>(null);
	const [stores, setStores] = useState<IStore[] | []>([]);
	const [isSkapaFocusLockDisabled, setIsSkapaFocusLockDisabled] = useState<boolean>(false);
	const onModalOpen = (e: CustomEvent): void => {
		setIsSkapaFocusLockDisabled(true);
		setVisibleModal(e.detail.visibleModal);
	};
	const shoppingItem = useSelector<RootState, ShoppingItem[]>(
		(state) => state.sofaConfiguration.shoppingItems
	);
	const onModalClose = (): void => {
		setIsSkapaFocusLockDisabled(false);
		setVisibleModal(null);
	};

	async function getShoppingProducts(): Promise<void> {
		const productService = new ProductService({
			locale: settings.locale,
			localizationPrefixApplication: settings.localizationPrefixApplication,
			localizationEndpoints: settings.localizationEndpoints,
		});

		const shoppingProducts = await productService.getShoppingProducts(shoppingItems, sofaFields);

		if (coverOnly) {
			const articleItems: ShoppingItem[] = [];
			shoppingProducts.forEach((prod) => {
				if (prod.product?.itemId?.includes('ART')) {
					articleItems.push({
						id: prod.product.itemId.replace('ART-', ''),
						quantity: prod.quantity,
						type: 'ART',
					});
				} else {
					prod.product.content?.child?.forEach((child) => {
						articleItems.push({
							id: child.itemId.replace('ART-', ''),
							quantity: child.quantity * prod.quantity,
							type: 'ART',
						});
					});
				}
			});

			const articleProducts = await productService.getShoppingProducts(
				articleItems,
				KompisDefaultFields
			);

			const filteredCoverProducts = articleProducts.filter(
				(prod) => prod.product.valid && prod.product.content?.mainTypeCode === '00062'
			);

			setShoppingProducts(filteredCoverProducts);
		} else {
			const filteredShoppingProducts = shoppingProducts.filter(
				(shoppingProduct) => shoppingProduct.product.valid
			);
			setShoppingProducts(filteredShoppingProducts);
		}
	}

	async function getStores(): Promise<void> {
		if (platform === 'kiosk') {
			setStores([]);
		} else {
			const storeService = new StoreService({
				locale: settings.locale,
				localizationPrefixApplication: settings.localizationPrefixApplication,
				localizationEndpoints: settings.localizationEndpoints,
				consumer: settings.consumer,
				contract: settings.contract,
			});
			setStores(await storeService.getStores());
		}
	}

	const onStoreSelectorChange = (e: CustomEvent): void => {
		dispatch(setStoreId(e.detail.storeId));
	};

	async function getStoreAvailability(): Promise<void> {
		if (storeId) {
			const checkoutAvailabilityService = new CheckoutAvailabilityService({
				locale: settings.locale,
				localizationEndpoints: settings.localizationEndpoints,
			});
			const storeAvailability = await checkoutAvailabilityService.getStoreAvailabilities(
				storeId,
				shoppingItem
			);
			const availability = storeAvailability.find(
				(item) => item.itemId.split('-')[1] === selectedArticle
			);
			setStoreAvailability(availability ? availability : null);
		} else {
			setStoreAvailability(null);
		}
	}

	useEffect(() => {
		getStoreAvailability();
	}, [storeId]);

	useEffect(() => {
		getShoppingProducts();
		getStores();
	}, [selectedArticle, coverOnly, storeId]);

	useEffect(() => {
		if (currentLightbox === 'additional-information') {
			setTimeout(() => {
				setVisible(true);
			}, 100);
		}
	}, [currentLightbox]);

	useEffect(() => {
		if (showScreensaver) {
			setVisibleModal(null);
		}
	}, [showScreensaver]);

	return (
		<AdditionalInformation
			settings={settings}
			platform={platform}
			deviceType={deviceType}
			orientation={orientation}
			productItem={productItem}
			carouselSelectedIndex={carouselSelectedIndex}
			showSheet={visible}
			visibleModal={visibleModal}
			kompisSettings={kompisSettings}
			kompisTranslations={kompisTranslations}
			shoppingProducts={shoppingProducts}
			storeAvailability={storeAvailability}
			storeId={storeId}
			stores={stores}
			isProduction={isProduction}
			isSkapaFocusLockDisabled={isSkapaFocusLockDisabled}
			onModalOpen={onModalOpen}
			onModalClose={onModalClose}
			onClose={(): void => {
				setVisible(false);
				setTimeout(() => {
					dispatch(setCurrentLightbox('none'));
					dispatch(selectSceneItem(null));
				}, 100);
			}}
			onCarouselChange={(carouselSelectedIndex): void => {
				const product = (shoppingProducts as IShoppingProduct[])[0].product;
				if (product.content?.image) {
					const images = product.content.image.filter((image) => image.size === 'S4');
					dispatch(
						setThumbnailImageChanged({
							imageUrl: images[carouselSelectedIndex].url,
							origin: 'additional-information',
						})
					);
				}
				setCarouselSelectedIndex(carouselSelectedIndex);
			}}
			onStoreSelectorChange={(e: CustomEvent): void =>
				onStoreSelectorChange && onStoreSelectorChange(e)
			}
		></AdditionalInformation>
	);
};
export default AdditionalInformationContainer;
